import type { FeedbackResponseFilters } from '@hotjar/feedback/components/IncomingFeedback/ResponsesFilters/ResponsesFilters';
import {
  decode,
  parseNumber,
  undelimit,
} from '@hotjar/feedback/domain/common/Responses/Filters/Query/queryUtils';
import { parseDateRange } from '@hotjar/feedback/domain/common/Responses/Filters/Query/GlobalFilterQuery/parseDateRange';
import { parseDeviceList } from '@hotjar/feedback/domain/common/Responses/Filters/Query/GlobalFilterQuery/parseDeviceList';
import { LargeListFilterOperation, RadioTextFilterOperation } from '@hotjar/global-filters';
import { parseCountryList } from '@hotjar/feedback/domain/common/Responses/Filters/Query/GlobalFilterQuery/parseCountryList';
import { RATING_TO_RATING_TYPE_ENUM } from '@hotjar/feedback/components/IncomingFeedback/ResponsesFilters/RatingFilter/RatingFilterType';
import { HAS_MESSAGE_TYPE } from '@hotjar/feedback/components/IncomingFeedback/ResponsesFilters/HasFeedbackMessageFilter/HasFeedbackMessageType';
import { HAS_EMAIL_TYPE } from '@hotjar/feedback/components/IncomingFeedback/ResponsesFilters/HasEmailFilter/HasEmailType';
import { RATING_TYPE } from '@hotjar/feedback/domain/Surveys/Responses/Filters/FilterDataType';
import { IncomingFeedbackFilters } from '../ResponsesFilters';
import type {
  CountryResponseFilter,
  DateResponseFilter,
  DeviceResponseFilter,
  HasEmailResponseFilter,
  HasMessageResponseFilter,
  KeywordResponseFilter,
  PageResponseFilter,
  RatingResponseFilter,
} from '../ResponsesFilters';

function parseDateFilter(params: URLSearchParams): DateResponseFilter | undefined {
  const dateRange = parseDateRange(params);
  if (dateRange === undefined) return;
  return {
    type: IncomingFeedbackFilters.DATE,
    value: dateRange,
  };
}

function parseDeviceFilter(params: URLSearchParams): DeviceResponseFilter | undefined {
  const deviceParam = params.get('device');

  if (deviceParam === null) return undefined;

  const deviceList = parseDeviceList(deviceParam);
  return {
    type: IncomingFeedbackFilters.DEVICE,
    value: deviceList,
  };
}

function parseCountryFilter(params: URLSearchParams): CountryResponseFilter | undefined {
  {
    const countryParam = params.get('country');

    if (countryParam !== null) {
      const countryList = parseCountryList(countryParam);
      return {
        type: IncomingFeedbackFilters.COUNTRY,
        value: {
          operation: LargeListFilterOperation.IS_ANY_OF_THESE,
          selectedValues: countryList,
        },
      };
    }
  }
  {
    const countryNotParam = params.get('country_not');

    if (countryNotParam !== null) {
      const countryList = parseCountryList(countryNotParam);
      return {
        type: IncomingFeedbackFilters.COUNTRY,
        value: {
          operation: LargeListFilterOperation.IS_NONE_OF_THESE,
          selectedValues: countryList,
        },
      };
    }
  }
  return undefined;
}

function parseRatingFilter(params: URLSearchParams): RatingResponseFilter | undefined {
  const ratingParam = params.get('rating');

  if (ratingParam === null) return undefined;

  if (ratingParam === 'all')
    return {
      type: IncomingFeedbackFilters.RATING,
      value: [
        RATING_TYPE.HATE,
        RATING_TYPE.DISLIKE,
        RATING_TYPE.NEUTRAL,
        RATING_TYPE.LIKE,
        RATING_TYPE.LOVE,
      ],
    };

  const ratings = new Set(
    undelimit(ratingParam)
      .map(parseNumber)
      .filter((num): num is number => num !== undefined)
      .map((num) => RATING_TO_RATING_TYPE_ENUM[String(num - 1)]),
  );

  return {
    type: IncomingFeedbackFilters.RATING,
    value: ratings.size > 0 ? Array.from(ratings) : undefined,
  };
}

function parsePageFilter(params: URLSearchParams): PageResponseFilter | undefined {
  {
    const urlParam = params.get('url');

    if (urlParam !== null) {
      return {
        type: IncomingFeedbackFilters.PAGE,
        value: { operation: RadioTextFilterOperation.IS, value: urlParam },
      };
    }
  }
  {
    const urlParam = params.get('url_contains');

    if (urlParam !== null) {
      return {
        type: IncomingFeedbackFilters.PAGE,
        value: { operation: RadioTextFilterOperation.CONTAINS, value: urlParam },
      };
    }
  }
  {
    const urlParam = params.get('url_not_contains');

    if (urlParam !== null) {
      return {
        type: IncomingFeedbackFilters.PAGE,
        value: { operation: RadioTextFilterOperation.NOT_CONTAINS, value: urlParam },
      };
    }
  }
  {
    const urlParam = params.get('url_starts_with');

    if (urlParam !== null) {
      return {
        type: IncomingFeedbackFilters.PAGE,
        value: { operation: RadioTextFilterOperation.STARTS_WITH, value: urlParam },
      };
    }
  }
  {
    const urlParam = params.get('url_ends_with');

    if (urlParam !== null) {
      return {
        type: IncomingFeedbackFilters.PAGE,
        value: { operation: RadioTextFilterOperation.ENDS_WITH, value: urlParam },
      };
    }
  }
  return undefined;
}

function parseKeywordFilter(params: URLSearchParams): KeywordResponseFilter | undefined {
  {
    const keywordContainsParam = params.get('keyword_contains');

    if (keywordContainsParam !== null) {
      const keywords = decode(keywordContainsParam);
      return {
        type: IncomingFeedbackFilters.KEYWORD,
        value: {
          value: keywords,
          operation: RadioTextFilterOperation.CONTAINS,
        },
      };
    }
  }
  {
    const keywordNotContainsParam = params.get('keyword_not_contains');

    if (keywordNotContainsParam !== null) {
      const keywords = decode(keywordNotContainsParam);
      return {
        type: IncomingFeedbackFilters.KEYWORD,
        value: {
          value: keywords,
          operation: RadioTextFilterOperation.NOT_CONTAINS,
        },
      };
    }
  }
  return undefined;
}

function parseHasMessageResponseFilter(
  params: URLSearchParams,
): HasMessageResponseFilter | undefined {
  const userFeedbackParam = params.get('has_message');

  if (
    userFeedbackParam === null ||
    (userFeedbackParam !== HAS_MESSAGE_TYPE.YES &&
      userFeedbackParam !== HAS_MESSAGE_TYPE.NO &&
      userFeedbackParam !== HAS_MESSAGE_TYPE.ANY)
  )
    return undefined;

  return {
    type: IncomingFeedbackFilters.FEEDBACK_MESSAGE,
    value: userFeedbackParam,
  };
}

function parseHasEmailResponseFilter(params: URLSearchParams): HasEmailResponseFilter | undefined {
  const hasEmailParam = params.get('has_email');

  if (
    hasEmailParam === null ||
    (hasEmailParam !== HAS_EMAIL_TYPE.YES &&
      hasEmailParam !== HAS_EMAIL_TYPE.NO &&
      hasEmailParam !== HAS_EMAIL_TYPE.ANY)
  )
    return undefined;

  return {
    type: IncomingFeedbackFilters.EMAIL,
    value: hasEmailParam,
  };
}

export function mergeFilterParams(
  defaultFilters: FeedbackResponseFilters,
  params: URLSearchParams | undefined,
): FeedbackResponseFilters {
  if (params === undefined) return defaultFilters;

  const filters: FeedbackResponseFilters = defaultFilters;

  filters[IncomingFeedbackFilters.DATE] =
    parseDateFilter(params) ?? filters[IncomingFeedbackFilters.DATE];
  filters[IncomingFeedbackFilters.DEVICE] =
    parseDeviceFilter(params) ?? filters[IncomingFeedbackFilters.DEVICE];
  filters[IncomingFeedbackFilters.COUNTRY] =
    parseCountryFilter(params) ?? filters[IncomingFeedbackFilters.COUNTRY];
  filters[IncomingFeedbackFilters.RATING] =
    parseRatingFilter(params) ?? filters[IncomingFeedbackFilters.RATING];
  filters[IncomingFeedbackFilters.PAGE] =
    parsePageFilter(params) ?? filters[IncomingFeedbackFilters.PAGE];
  filters[IncomingFeedbackFilters.KEYWORD] =
    parseKeywordFilter(params) ?? filters[IncomingFeedbackFilters.KEYWORD];
  filters[IncomingFeedbackFilters.FEEDBACK_MESSAGE] =
    parseHasMessageResponseFilter(params) ?? filters[IncomingFeedbackFilters.FEEDBACK_MESSAGE];
  filters[IncomingFeedbackFilters.EMAIL] =
    parseHasEmailResponseFilter(params) ?? filters[IncomingFeedbackFilters.EMAIL];

  return filters;
}
